import {
  useMutation,
  useMutationState,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import {
  CreateCandidateRequest,
  GetInterviewResponse,
  UpdateCandidateRequest,
  UpdateCandidateResponse,
  UploadCandidateCVRequest,
  UploadCandidatePictureRequest,
} from '@pkg/types/api'

import api from '@/api'
import { INTERVIEW_KEY, useInterview } from '@/react-query/interview/interview'

export const CANDIDATE_KEY = 'candidate'
const UPLOAD_CANDIDATE_CV_KEY = ['uploadCandidateCV']

const MINUTE = 1000 * 60
export const useCandidate = () => {
  const candidateId = useInterview().data?.candidateId

  return useQuery({
    queryKey: [CANDIDATE_KEY, candidateId],
    queryFn: async () => {
      if (!candidateId) {
        return null
      }
      return (await api.getCandidate(candidateId)).data.candidate
    },
    enabled: !!candidateId,
    staleTime: 5 * MINUTE,
  })
}

export const useUpdateCandidate = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: UpdateCandidateRequest) => {
      const response = await api.updateCandidate(data)
      return response.data.candidate
    },
    onMutate: (data) => {
      queryClient.setQueryData(
        [CANDIDATE_KEY, data.candidate.id],
        (oldCandidate: UpdateCandidateResponse['candidate']) => ({
          ...oldCandidate,
          ...data.candidate,
        }),
      )
    },
    onError: (_, variables) => {
      return queryClient.invalidateQueries({
        queryKey: [CANDIDATE_KEY, variables.candidate.id],
      })
    },
  })
}

export const useCreateCandidate = () => {
  const queryClient = useQueryClient()
  const interviewId = useInterview().data?.id

  return useMutation({
    mutationFn: async (data: Omit<CreateCandidateRequest, 'interviewId'>) => {
      if (!interviewId) {
        return
      }
      const response = await api.createCandidate({
        candidate: data.candidate,
        interviewId,
      })
      queryClient.setQueryData(
        [CANDIDATE_KEY, response.data.candidate.id],
        response.data.candidate,
      )
      queryClient.setQueryData(
        [INTERVIEW_KEY, interviewId],
        (oldData: GetInterviewResponse['interview']) => ({
          ...oldData,
          candidateId: response.data.candidate.id,
        }),
      )
      return response.data.candidate
    },
  })
}

export const useUploadCandidatePicture = () => {
  const queryClient = useQueryClient()
  const candidateId = useInterview().data?.candidateId

  return useMutation({
    mutationFn: async (data: UploadCandidatePictureRequest) => {
      if (!candidateId) {
        throw new Error('Candidate should be defined to upload picture')
      }
      data.append('candidateId', candidateId)
      const response = await api.uploadCandidatePicture(data)
      return response.data.candidate
    },
    onSuccess: (candidate) => {
      queryClient.setQueryData(
        [CANDIDATE_KEY, candidate.id],
        (oldCandidate: UpdateCandidateResponse['candidate']) => ({
          ...oldCandidate,
          PictureAlternatives: candidate.PictureAlternatives,
          profilePictureURL: candidate.profilePictureURL,
        }),
      )
    },
  })
}

type UploadCandidateCVVariables = { candidateId: string; files: File[] }

export const useUploadCandidateCV = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: UPLOAD_CANDIDATE_CV_KEY,
    mutationFn: async ({ candidateId, files }: UploadCandidateCVVariables) => {
      if (!candidateId) {
        throw new Error('Candidate should be defined to upload files')
      }
      if (!files.length) {
        return (await api.getCandidate(candidateId)).data.candidate
      }

      const formData = new FormData() as UploadCandidateCVRequest
      files.forEach((file) => formData.append('files', file))
      formData.append('candidateId', candidateId)

      const response = await api.uploadCandidateCV(formData)
      return response.data.candidate
    },
    onSuccess: (candidate) => {
      queryClient.setQueryData(
        [CANDIDATE_KEY, candidate.id],
        (oldCandidate: UpdateCandidateResponse['candidate']) => ({
          ...oldCandidate,
          ...candidate,
        }),
      )
    },
  })
}

/**
 * Names of the files still uploading for this candidate, whichever component
 * started the upload (the create form unmounts as soon as the candidate exists).
 */
export const useUploadingCandidateCVNames = (candidateId?: string) =>
  useMutationState({
    filters: { mutationKey: UPLOAD_CANDIDATE_CV_KEY, status: 'pending' },
    select: (mutation) =>
      mutation.state.variables as UploadCandidateCVVariables | undefined,
  })
    .filter((variables) => variables?.candidateId === candidateId)
    .flatMap((variables) => variables?.files.map((file) => file.name) ?? [])

/** Removes the file from the card at once and puts it back if the delete fails. */
export const useDeleteCandidateCV = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      candidateId,
      cvId,
    }: {
      candidateId: string
      cvId: string
    }) => {
      const response = await api.deleteCandidateCV(candidateId, cvId)
      return response.data.candidate
    },
    onMutate: ({ candidateId, cvId }) => {
      queryClient.setQueryData(
        [CANDIDATE_KEY, candidateId],
        (oldCandidate?: UpdateCandidateResponse['candidate']) =>
          oldCandidate && {
            ...oldCandidate,
            CVs: oldCandidate.CVs?.filter((cv) => cv.id !== cvId),
          },
      )
    },
    onError: (_, { candidateId }) =>
      queryClient.invalidateQueries({ queryKey: [CANDIDATE_KEY, candidateId] }),
    onSuccess: (candidate) => {
      queryClient.setQueryData(
        [CANDIDATE_KEY, candidate.id],
        (oldCandidate: UpdateCandidateResponse['candidate']) => ({
          ...oldCandidate,
          fullName: candidate.fullName,
          CVs: candidate.CVs,
        }),
      )
    },
  })
}
